/**
 * 站台機密（批151 C6）的共用顯示邏輯——站台詳情、〈全部站台〉的反查框、開站精靈與代管核可共用。
 *
 * ★ 後台**永遠拿不到值**：只有「已設定／末四碼／版本／套用版本」。值只在送出的那一次請求裡
 *   出現，輸入框一律 `type=password`、不回填、送出後清空。
 * ★ 錯誤訊息一律不回聲輸入（後端也是）——這裡的對照表只放「可以多指一步路」的碼，
 *   其餘照後端的中文訊息（它已經帶欄位名，例如「『某欄』：機密值不可包含空白」）。
 */
import { ApiError } from '@/api/client'
import type { SiteSecretField } from '@/api/sites'
import { SITE_SECRET_RELEASED_ALERT, alertKindLabel } from '@/lib/fleet'

/** 欄位的「套用狀態」需要的那幾欄（站台詳情的欄位列）。 */
type AppliedLike = Pick<SiteSecretField, 'configured' | 'version' | 'applied_version'>

/** 版本是毫秒時間戳——顯示成本地時間（`YYYY-MM-DD HH:mm:ss`），比一串數字好認。 */
export function secretVersionText(version: number | null | undefined): string {
  if (version == null || !Number.isFinite(version)) return '—'
  const d = new Date(version)
  if (Number.isNaN(d.getTime())) return String(version)
  const p2 = (n: number) => String(n).padStart(2, '0')
  return (
    `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())} ` +
    `${p2(d.getHours())}:${p2(d.getMinutes())}:${p2(d.getSeconds())}`
  )
}

export type AppliedTone = 'none' | 'ok' | 'pending'

/**
 * 套用狀態：「未設定」／「已套用（版本）」／「尚未套用」。
 *
 * 站台上留著**較舊的版本**（換了新值、agent 還沒寫進去）也算「尚未套用」，另外講站台上是哪一版
 * ——那正是「換了鑰匙但站台還在用舊的」要被看見的時候。
 */
export function secretAppliedText(f: AppliedLike): { text: string; tone: AppliedTone } {
  if (!f.configured) return { text: '未設定', tone: 'none' }
  if (f.applied_version != null && f.applied_version === f.version) {
    return { text: `已套用（版本 ${secretVersionText(f.version)}）`, tone: 'ok' }
  }
  if (f.applied_version != null) {
    return {
      text: `尚未套用（站台上是較舊的版本 ${secretVersionText(f.applied_version)}）`,
      tone: 'pending',
    }
  }
  return { text: '尚未套用', tone: 'pending' }
}

/** 已設定時的「末四碼」欄文字（值太短時後端不給末四碼）。 */
export function secretLast4Text(f: Pick<SiteSecretField, 'configured' | 'last4'>): string {
  if (!f.configured) return '—'
  return f.last4 ? `…${f.last4}` : '已設定（值較短，不顯示末四碼）'
}

// --- 欄位列的動作（站台詳情） ---------------------------------------------------

/**
 * 一個欄位列要顯示哪些按鈕。`canManage`＝營運主管以上且看得到這一區；不是的話一顆都沒有
 * （admin 只看狀態）。
 *
 * | 動作 | 何時有 |
 * |---|---|
 * | `set`（設定／更換） | 欄位仍在產品宣告裡 |
 * | `redeliver`（重送）、`clear`（清除） | 授權中心有這個欄位的值 |
 * | `clearResidue`（清除站上殘留，裁示 126①） | 已宣告、但授權中心**沒有**值——站台 `.env` 可能還留著舊的那一行 |
 * | `release`（只釋出、不派工，裁示 126②） | 授權中心有這個欄位的紀錄（沒有紀錄就沒有東西可釋出；協調裁定只在已設定時顯示） |
 */
export interface SecretRowActions {
  set: boolean
  redeliver: boolean
  clear: boolean
  clearResidue: boolean
  release: boolean
}

export function secretRowActions(
  f: Pick<SiteSecretField, 'declared' | 'configured'>,
  canManage: boolean,
): SecretRowActions {
  if (!canManage) return { set: false, redeliver: false, clear: false, clearResidue: false, release: false }
  return {
    set: f.declared,
    redeliver: f.configured,
    clear: f.configured,
    clearResidue: f.declared && !f.configured,
    release: f.configured,
  }
}

type FieldLike = Pick<SiteSecretField, 'label' | 'env' | 'last4' | 'configured'>

function fieldText(f: FieldLike): string {
  return `「${f.label}」（${f.env}${f.configured && f.last4 ? `，末四碼 ${f.last4}` : ''}）`
}

/** 「清除站上殘留」的確認文案（裁示 126①）。 */
export function clearResidueConfirmMessage(slug: string, f: FieldLike): string {
  return (
    `站台「${slug}」的${fieldText(f)}在授權中心沒有設定值，但站台上可能還留著舊值` +
    '（例如開站失敗時授權中心已先釋出）。\n\n' +
    `確定後會派工請 agent 把站台 .env 的這一行（${f.env}）刪掉；站台裡依賴它的功能會失效。` +
    '授權中心這邊沒有紀錄可刪，也不影響其他站台。'
  )
}

/** 「只釋出（不派工）」的確認文案（裁示 126②）。 */
export function releaseConfirmMessage(slug: string, f: FieldLike): string {
  return (
    `確定只釋出站台「${slug}」的${fieldText(f)}？\n\n` +
    '只在授權中心刪除紀錄並釋出唯一性（同一把值之後可以給別的站台），' +
    '站台上的值不會被移除，也不會派任何任務給主機。\n' +
    `用於開站卡住或站台已不再使用時；站台若之後恢復運作，會出現「${alertKindLabel(SITE_SECRET_RELEASED_ALERT)}」的告警。`
  )
}

// --- 反查 ---------------------------------------------------------------------

export type LookupMode = 'value' | 'last4'

/** 與後端 `site_secret_box` 同一組長度（反查以完整值查詢時同樣要過值的規則）。 */
export const SECRET_MIN_LEN = 8
export const SECRET_MAX_LEN = 512

/**
 * 反查框送出前的檢查＋組 body。兩種模式只送其中一個鍵（後端 `{value}` 與 `{last4}` 擇一，
 * 兩個都給是 422 `lookup_one_of`）。錯誤訊息不含輸入值。
 */
export function lookupBody(
  mode: LookupMode,
  raw: string,
): { ok: true; body: { value: string } | { last4: string } } | { ok: false; error: string } {
  const text = raw.trim()
  if (!text) return { ok: false, error: mode === 'last4' ? '請輸入末四碼。' : '請輸入完整的機密值。' }
  if (mode === 'last4') {
    if (text.length !== 4 || /\s/.test(text)) return { ok: false, error: '末四碼必須剛好是 4 個字元（不含空白）。' }
    return { ok: true, body: { last4: text } }
  }
  if (text.length < SECRET_MIN_LEN || text.length > SECRET_MAX_LEN) {
    return { ok: false, error: `完整的機密值長度介於 ${SECRET_MIN_LEN}～${SECRET_MAX_LEN} 字。` }
  }
  return { ok: true, body: { value: text } }
}

// --- 錯誤訊息 -------------------------------------------------------------------

/**
 * 站台機密相關的錯誤碼 → 給操作者的一句話（含下一步）。查不到的碼退回後端訊息。
 *
 * ★ 刻意**不收** `role_required`：開站時這個碼可能來自站台機密閘，也可能來自開站內容閘，
 *   兩者的指路不同——由各宿主自己決定（後端訊息本身已經寫清楚是哪一道）。
 */
export const SITE_SECRET_ERROR_MESSAGES: Record<string, string> = {
  secret_in_use:
    '這把機密已被其他在役站台（或本站的另一個欄位）使用：同一把機密不可同時給兩處。營運主管可在〈全部站台〉上方的機密反查找出使用中的站台。',
  agent_unsupported:
    '這台主機的 agent 不支援站台機密：請先升級 agent，或請營運主管在〈代管種類〉登錄支援站台機密的最低版本。',
  reseller_host: '這台是代理商自備的主機：我方的站台機密不能放到代理商的機器上。',
  site_not_ready: '只有「運行中」或「已停站」的站台能設定站台機密；請等站台開好（或處理完異常）再設定。',
  provision_in_progress: '這一站的開站任務還沒結束；請等開站完成再設定站台機密。',
  site_secret_key_unavailable:
    '授權中心尚未設定站台機密的加密主鑰（IPO_LS_SITE_SECRET_KEY），站台機密功能暫停（其他功能照常）；請洽系統管理者補上。',
  site_secrets_unsupported: '這個產品沒有宣告任何站台機密欄位。',
  api_key_web_only: '站台機密只能在後台網頁操作。',
  no_secrets: '這一站沒有任何已設定的站台機密可以重送。',
  /** 裁示 126：同一個欄位被別人同時改了（設定／清除／釋出撞在一起）。 */
  secret_conflict: '這個站台機密剛被其他人改過，請重新整理後再試。',
}

export function apiDetail(e: unknown): { code?: string; message?: string } & Record<string, unknown> {
  const d = e instanceof ApiError && e.payload ? (e.payload as { detail?: unknown }).detail : null
  if (typeof d === 'string') return { code: d, message: d }
  return (d ?? {}) as { code?: string; message?: string } & Record<string, unknown>
}

/** 錯誤 → 中文一句話：對照表 ＞ 後端訊息 ＞ fallback。 */
export function siteSecretErrText(e: unknown, fallback: string): string {
  const d = apiDetail(e)
  if (d.code && SITE_SECRET_ERROR_MESSAGES[d.code]) return SITE_SECRET_ERROR_MESSAGES[d.code]
  if (d.code === 'role_required') return '站台機密的設定、更換、清除、重送與反查只有「營運主管」以上可以操作。'
  if (typeof d.message === 'string' && d.message) return d.message
  return fallback
}

/**
 * 開站／核可時的站台機密欄**為什麼不能填**（`null`＝可以填）。
 *
 * 判準來自最近一次同產品的 preflight：`site_secrets_supported=false`（代理商主機或 agent 不支援）、
 * `secret_key_available=false`（授權中心沒有加密主鑰）。還沒有 preflight＝還不知道這台主機能不能帶。
 */
export function provisionSecretBlocker(
  pre: { site_secrets_supported?: boolean; secret_key_available?: boolean } | null | undefined,
  pendingText = '完成主機與站台代號的檢查後，才知道這台主機能不能帶站台機密。',
): string | null {
  if (!pre) return pendingText
  if (pre.secret_key_available === false) {
    return '授權中心尚未設定站台機密的加密主鑰（IPO_LS_SITE_SECRET_KEY），暫時不能設定；可以先開站，之後再到站台詳情設定。'
  }
  if (pre.site_secrets_supported === false) {
    return '這台主機不能帶站台機密（代理商自備的主機，或 agent 版本不支援）；可以先開站，之後再到站台詳情設定。'
  }
  return null
}

/** 開站時的站台機密欄位宣告（preflight 的 `site_secret_fields` 或產品代管設定的 `site_secrets`）。 */
export interface SecretFieldDecl {
  key: string
  env: string
  label: string
  hint?: string | null
}

/**
 * 從產品代管設定（原始 JSON）讀出 `site_secrets` 宣告——preflight 還沒回來時的退路。
 * 形狀不對的項目一律略過（權威驗證在後端 `services/hosting_profile.py`）。
 */
export function declaredSecretFields(rawProfile: unknown): SecretFieldDecl[] {
  const list = (rawProfile as { site_secrets?: unknown } | null | undefined)?.site_secrets
  if (!Array.isArray(list)) return []
  const out: SecretFieldDecl[] = []
  for (const item of list) {
    const o = item as Record<string, unknown> | null
    if (!o || typeof o.key !== 'string' || typeof o.env !== 'string') continue
    out.push({
      key: o.key,
      env: o.env,
      label: typeof o.label === 'string' && o.label ? o.label : o.key,
      hint: typeof o.hint === 'string' ? o.hint : null,
    })
  }
  return out
}

/** 只留有填值的欄位（空白＝這一欄這次不帶）；一個都沒有回 `null`（送出時整個鍵不帶）。 */
export function filledSecrets(values: Record<string, string>): Record<string, string> | null {
  const out: Record<string, string> = {}
  for (const [k, v] of Object.entries(values)) {
    if (typeof v === 'string' && v.trim()) out[k] = v.trim()
  }
  return Object.keys(out).length ? out : null
}
